import type {
  DeliveryManagerRow,
  DeliveryRecruiterRow,
  MetricsBlock,
  UnattributedBooks,
} from '../../api/types.js';
import {
  REGION_NAMES,
  accentFor,
  color,
  font,
  fontSize,
  initialsOf,
  isUiRegion,
  personAccent,
  radius,
} from '../../theme/tokens.js';
import { breakdownRows, metricValue } from '../Breakdown/Breakdown.js';
import { DataQualityBadge } from '../DataQualityBadge/DataQualityBadge.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { MetricCell, formatValue, money } from '../MetricCell/MetricCell.js';
import {
  TRENDED_TRACK_PX,
  TRENDED_TWO_FIGURE_TRACK,
  TRENDED_TWO_FIGURE_TRACK_PX,
} from '../RegionTable/RegionTable.js';

/**
 * The Delivery drill-down tables (FR-D14, FR-D15, FR-D23, PRD-Delivery §9.1).
 *
 * Two variants over one column vocabulary:
 *
 * ```text
 * managers   TOA · Onb · Rec · Rec-N · Rec-A · BoB · PEND · PROS · PEMP
 *            Subs · Subs-I · Subs-T · Ofrs · Ofrs-I · Ofrs-T · Strt · Strt-I · Strt-T
 *            GM · GM-I · GM-T                                            (21 metric columns)
 *
 * recruiters the same, minus Rec / Rec-N / Rec-A — a person is not a headcount —
 *            plus TOA-I and TOA-T immediately after TOA                  (20 metric columns)
 *            plus the two wide Primary / Secondary target-account columns the blueprint's
 *            recruiter grid carries beside the name
 * ```
 *
 * The account columns are in the blueprint (`180px 130px 130px repeat(20, …)`) and are outside
 * PRD-Delivery §9.1's list, which enumerates *metric* columns only — so Principle I includes them
 * and the PRD does not exclude them. They repeat what the recruiter detail shows (§6.5), which is
 * the point: a manager scanning a team should not have to open six pages to see who holds what.
 *
 * Three rules inherited structurally rather than re-implemented:
 *
 * - **Every column carries a `fullLabel`**, because `<Column>` cannot be declared without one.
 *   Gate 2 exempts no column, and the type checker is what enforces it.
 * - **Every metric cell's breakdown opens on hover *and* keyboard focus**, because it goes
 *   through `<MetricCell>` → `<Disclosure>` (FR-D15). Nothing here is pointer-only.
 * - **`—` for null, never `0`** (FR-055, FR-D22). A recruiter with no book is listed with em
 *   dashes across the row: existing and holding a book are different facts.
 *
 * **Trend arrows on six columns only** — TOA, Onb, Subs, Ofrs, Strt, GM (PRD-Delivery §9.1). The
 * `-I` and `-T` columns are *decompositions* of those six, so they show the figure without
 * asserting a second comparison beside it. Their 30- and 90-day changes stay in the tooltip.
 *
 * ⚠ **No Jobs column, on either table.** A job has no recruiter — it reaches a region through its
 * client — so it cannot be attributed to a person (FR-D09). The chart omits the series for the
 * same reason, which is what keeps the two halves of the page consistent.
 *
 * ⚠ **No health, compliance, or flag column.** Book-health flagging is out of scope with recorded
 * evidence, not forgotten (FR-D17).
 */

/**
 * Region codes as a **set**: 22 of 24 managers span regions (PRD-Delivery §7.3).
 *
 * Filtered to the regions this client renders, which is the only place Delivery needs it: the
 * hierarchy is still built over the server's seven, so a code it does not show would arrive here
 * as a badge in the fallback accent — a grey-teal `SPA` beside five coloured ones. Dropping it in
 * the badge rather than dropping the row is deliberate: the row's figures are in the totals
 * beneath it, and a person removed from a table to tidy up a label is a person unaccounted for.
 */
function RegionCodes({ codes: served }: { codes: string[] }) {
  const codes = served.filter(isUiRegion);

  if (codes.length === 0) {
    return <span style={{ fontSize: '10.5px', color: color.inkFaint }}>no regions</span>;
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      {codes.map((code) => (
        <Disclosure
          key={code}
          // Announced as "R3, Midwest" — the code alone tells a screen-reader user nothing.
          accessibleName={`${code}, ${REGION_NAMES[code]}`}
        >
          <span
            className="mono"
            style={{
              fontFamily: font.mono,
              fontSize: '9px',
              fontWeight: 600,
              color: color.onAccent,
              background: accentFor(code),
              padding: '1px 5px',
              borderRadius: radius.sm,
            }}
          >
            {code}
          </span>
        </Disclosure>
      ))}
    </span>
  );
}

/**
 * The trended columns — the ones that draw an arrow and its 30-day percentage. Everything else
 * shows its figure alone.
 *
 * ⚠ **Onb left this list on 2026-09-27** although PRD-Delivery §9.1 puts an arrow on it. It never
 * draws one: since 2026-09-03 it counts placements booked and not yet started, and run rate keeps
 * no status history, so `d30` is always `null` (FR-D23). While the trend slot was an 8px arrow that
 * cost nothing; at `TREND_SLOT_PX` (36px) it is a permanent gap pushing every Onb figure off its
 * right-aligned header. The day run rate gains a history, it comes back here and takes a
 * `TRENDED_TRACK_PX` floor like the others.
 */
const TRENDED = new Set(['activeToa', 'subs', 'offers', 'starts', 'gm']);

/** Columns that take no source or targeting axis, so their breakdown is the specialty split. */
const SPECIALTY_ONLY = new Set([
  'recruiters',
  'recruitersNursing',
  'recruitersAllied',
  'bob',
  // ⚠ `onboarding` left this set on 2026-09-03 — it reads run rate now, so Internal and Target
  // are real narrowings of it rather than the total restated under two more names.
  'pending',
  'prospect',
  'prevEmployed',
]);

/** Columns with no breakdown at all — the headcounts (PRD-Delivery §9.1). */
const NO_BREAKDOWN = new Set(['recruitersNursing', 'recruitersAllied']);

interface Spec {
  key: string;
  label: string;
  fullLabel: string;
  bold?: boolean;
  /** Marks the figures a shortfall in book attribution under-reports (FR-064). */
  dataQuality?: boolean;
  format?: (value: number) => string;
  /**
   * A second figure rendered after the first as `(n)` — only `subs(uniqueSubs)` today, the same
   * pair Region Performance's `#SUBS` renders (FR-084). The column then needs the two-figure
   * track, and the hover panel still decomposes the FIRST figure alone: unique counts do not
   * decompose (a candidate submitted to a Nursing and an Allied job counts in both halves).
   */
  suffixKey?: string;
}

/**
 * What a blueprint-default track gets in these tables, measured against the narrower recruiter
 * table — (1,900 − 450) / 20 ≈ 72px a column.
 */
const DEFAULT_TRACK_PX = 72;

/**
 * GM's face is `$12.35K` — seven glyphs where a count's widest is six — so its trended floor is
 * one glyph wider than `TRENDED_TRACK_PX`.
 */
const TRENDED_MONEY_TRACK_PX = TRENDED_TRACK_PX + 7;

/** The floor a column's own track takes, or `null` for the blueprint default. */
function floorPx(spec: Spec): number | null {
  if (spec.suffixKey) return TRENDED_TWO_FIGURE_TRACK_PX;
  if (!TRENDED.has(spec.key)) return null;
  return spec.format === money ? TRENDED_MONEY_TRACK_PX : TRENDED_TRACK_PX;
}

/**
 * How much wider than 1,900px a table has to be for its floored columns to keep their floors
 * **without** taking the width off their untrended siblings — the same reason Region Performance
 * declares its own minimum. Summed over the table's own specs, so the manager and recruiter tables
 * each pay for exactly the columns they carry.
 *
 * ⚠ It was one term until 2026-09-27 — the two-figure `Subs` track over the default. The trend
 * slot beside each trended figure (`▲12%`, 36px where the arrow alone was 8px) is what added the
 * others: at ~72px a trended count's face overran into the cell's `overflow: hidden`.
 */
function extraWidthPx(specs: Spec[]): number {
  return specs.reduce((sum, spec) => {
    const floor = floorPx(spec);
    return floor === null ? sum : sum + Math.max(0, floor - DEFAULT_TRACK_PX);
  }, 0);
}

const TOA: Spec = { key: 'activeToa', label: 'TOA', fullLabel: 'Active TOA — Travelers on Assignment', bold: true };
const TOA_I: Spec = { key: 'toaInternal', label: 'TOA-I', fullLabel: 'Active TOA — Internal accounts' };
const TOA_T: Spec = { key: 'toaTarget', label: 'TOA-T', fullLabel: 'Active TOA — Target accounts' };
// No `dataQuality`: a shortfall in *book* attribution cannot under-report a placement count.
const ONB: Spec = { key: 'onboarding', label: 'Onb', fullLabel: 'Candidates Onboarding' };
const REC: Spec = { key: 'recruiters', label: 'Rec', fullLabel: 'Recruiters', bold: true };
const REC_N: Spec = { key: 'recruitersNursing', label: 'Rec-N', fullLabel: 'Recruiters — Nursing' };
const REC_A: Spec = { key: 'recruitersAllied', label: 'Rec-A', fullLabel: 'Recruiters — Allied' };

const BOOK: Spec[] = [
  { key: 'bob', label: 'BoB', fullLabel: 'Total Candidates in BoB', bold: true, dataQuality: true },
  { key: 'pending', label: 'PEND', fullLabel: 'Pending Candidates', dataQuality: true },
  { key: 'prospect', label: 'PROS', fullLabel: 'Prospect Candidates', dataQuality: true },
  { key: 'prevEmployed', label: 'PEMP', fullLabel: 'Previously Employed', dataQuality: true },
];

const ACTIVITY: Spec[] = [
  // `Subs(Unique)` — submissions in the period, then the distinct candidates behind them.
  { key: 'subs', label: 'Subs', fullLabel: 'Total Subs (Unique)', suffixKey: 'uniqueSubs' },
  { key: 'subsInternal', label: 'Subs-I', fullLabel: 'Submissions — Internal accounts' },
  { key: 'subsTarget', label: 'Subs-T', fullLabel: 'Submissions — Target accounts' },
  { key: 'offers', label: 'Ofrs', fullLabel: 'Offers' },
  { key: 'offersInternal', label: 'Ofrs-I', fullLabel: 'Offers — Internal accounts' },
  { key: 'offersTarget', label: 'Ofrs-T', fullLabel: 'Offers — Target accounts' },
  { key: 'starts', label: 'Strt', fullLabel: 'Starts' },
  { key: 'startsInternal', label: 'Strt-I', fullLabel: 'Starts — Internal accounts' },
  { key: 'startsTarget', label: 'Strt-T', fullLabel: 'Starts — Target accounts' },
  { key: 'gm', label: 'GM', fullLabel: 'Gross Margin', format: money },
  { key: 'gmInternal', label: 'GM-I', fullLabel: 'Gross Margin — Internal accounts', format: money },
  { key: 'gmTarget', label: 'GM-T', fullLabel: 'Gross Margin — Target accounts', format: money },
];

const MANAGER_SPECS: Spec[] = [TOA, ONB, REC, REC_N, REC_A, ...BOOK, ...ACTIVITY];
const RECRUITER_SPECS: Spec[] = [TOA, TOA_I, TOA_T, ONB, ...BOOK, ...ACTIVITY];

/**
 * The per-row export control's track (FR-D27, owner request 2026-09-12).
 *
 * ⚠ **It fits here without any of the argument the region table needed.** That one had to weigh
 * 40px against a measured 1,345px honest bound; both Delivery tables already declare a
 * `minWidth` of 1,900px for 21 metric columns and scroll sideways at every laptop width by
 * design. 1,940px changes nothing about when they scroll — which is also why the control could
 * not go anywhere else: there is no chart-card header shared by these two tables, and the
 * question "export whose submissions" is answered by the row, not by the card.
 */
const ACTION_COLUMN_WIDTH = 68;

/**
 * The actions column, built once for both tables.
 *
 * ⚠ **The row is itself a button, so anything interactive inside it has to stop the event
 * escaping.** `<DataTable>` puts an `onClick` and an Enter/Space `onKeyDown` on the row `div` —
 * on Managers that navigates to the manager's page, on Recruiters it rescopes the chart (FR-D10)
 * — so without this the export fired *and* the page moved out from under the reader.
 *
 * ⚠ **The wrapper goes around the whole control, not just the trigger.** The completion dialog
 * renders inside this subtree, `position: fixed` notwithstanding: fixed positioning moves where a
 * node *paints*, never where its events go. So "Open sheet", "Close" and the scrim all bubble
 * here too, and wrapping only the button would mean finishing an export and having the click
 * that opens the sheet also navigate the page.
 *
 * ⚠ **Only Enter and Space are stopped, never every key.** React's `stopPropagation` calls the
 * native one, so blanket-stopping `keydown` would also keep Escape from reaching the `window`
 * listener that closes that dialog. Those two are exactly what the row listens for.
 *
 * ⚠ **`fullLabel` names the COLUMN and must not open with the word the row controls open with**
 * (2026-09-14). `<DataTable>` hands every header's full label to a `<Disclosure>`, which exposes
 * it as the header's accessible name — so a full label of "Export this manager's submissions…"
 * put a third `Export …` control into a table holding two, and a reader tabbing the header row
 * met something that sounds like the button in the cells below it. Hence the `Action — ` prefix:
 * the column's own name first, what its cells do after.
 */
function actionColumn<T>(
  render: (entity: T) => React.ReactNode,
  fullLabel: string,
): Column<Row<T>> {
  return {
    key: 'actions',
    label: 'Action',
    fullLabel,
    align: 'center',
    width: `${ACTION_COLUMN_WIDTH}px`,
    sortable: false,
    render: (row) => (
      <div
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
        }}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        {render(row.entity)}
      </div>
    ),
    /*
     * ⚠ No `renderTotal`. The totals row is a sum over people and has no scope of its own to
     * export — the toolbar button above the table is what covers "everyone in this table", and
     * a disabled control on that row would invite a reader to look for a reason it is disabled.
     */
  };
}

interface Siblings {
  internal?: MetricsBlock;
  target?: MetricsBlock;
  nursing?: MetricsBlock;
  allied?: MetricsBlock;
}

interface Row<T> {
  entity: T;
  metrics: MetricsBlock;
  siblings: Siblings;
}

/** The displayed combo plus its three single-axis siblings — the breakdown's source. */
function siblingsOf(byCombo: Record<string, MetricsBlock> | undefined, combo: string): Siblings {
  const [source, targeting, specialty] = combo.split('|');
  return {
    internal: byCombo?.[`internal|${targeting}|${specialty}`],
    target: byCombo?.[`${source}|target|${specialty}`],
    nursing: byCombo?.[`${source}|${targeting}|nursing`],
    allied: byCombo?.[`${source}|${targeting}|allied`],
  };
}

function metricColumn<T>(
  spec: Spec,
  unattributedBooks: UnattributedBooks | null | undefined,
  totals: MetricsBlock | undefined,
): Column<Row<T>> {
  const specialtyOnly = SPECIALTY_ONLY.has(spec.key);
  const suffixOf = (block: MetricsBlock | undefined) => {
    const value = spec.suffixKey ? block?.[spec.suffixKey]?.value : null;
    return value != null ? `(${formatValue(value)})` : undefined;
  };

  return {
    key: spec.key,
    label: spec.label,
    fullLabel: spec.fullLabel,
    align: 'right',
    ...(spec.suffixKey
      ? { width: TRENDED_TWO_FIGURE_TRACK }
      : floorPx(spec) !== null
        ? { width: `minmax(${floorPx(spec)}px, 1fr)` }
        : {}),
    render: (row) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
        <MetricCell
          metric={row.metrics[spec.key]}
          label={spec.fullLabel}
          metricKey={spec.key}
          bold={spec.bold}
          format={spec.format}
          suffix={suffixOf(row.metrics)}
          showTrendArrow={TRENDED.has(spec.key)}
          breakdown={
            NO_BREAKDOWN.has(spec.key)
              ? undefined
              : breakdownRows({
                  total: metricValue(row.metrics, spec.key),
                  internal: metricValue(row.siblings.internal, spec.key),
                  target: metricValue(row.siblings.target, spec.key),
                  nursing: metricValue(row.siblings.nursing, spec.key),
                  allied: metricValue(row.siblings.allied, spec.key),
                  // Book and headcount columns take the specialty axis only (PRD-Delivery §8.3),
                  // so an Internal or Target row there would restate the total under another name.
                  includeSource: !specialtyOnly,
                  includeTargeting: !specialtyOnly,
                })
          }
        />
        {spec.dataQuality ? (
          <DataQualityBadge unattributedBooks={unattributedBooks} figureLabel={spec.fullLabel} />
        ) : null}
      </div>
    ),
    sortValue: (row) => row.metrics[spec.key]?.value ?? null,
    renderTotal: totals
      ? () => (
          <MetricCell
            metric={totals[spec.key]}
            label={`Total ${spec.fullLabel}`}
            metricKey={spec.key}
            bold
            format={spec.format}
            suffix={suffixOf(totals)}
            /*
             * Trended columns reserve the slot here too, or a right-aligned total sits 36px to the
             * right of the figures above it. The all-managers sum serves no trend (`sumBlocks`
             * nulls it, since percentages do not add), so its slot stays empty; on a leader's or
             * a manager's page the row IS that scope's own block and its trend is genuine.
             */
            showTrendArrow={TRENDED.has(spec.key)}
          />
        )
      : undefined,
  };
}

/**
 * A target-account name, truncated to the column and reachable in full on hover or focus.
 *
 * `—` for absence, never the source's `"No Target Account"` placeholder: a recruiter with no
 * secondary account and one whose account is literally named that must stay distinguishable.
 */
function AccountCell({ value }: { value: string | null }) {
  return (
    <div style={{ padding: '9px 8px', minWidth: 0 }}>
      <Disclosure accessibleName={value ?? 'No target account'}>
        <span
          style={{
            fontSize: fontSize.caption,
            color: value ? color.inkRow : color.inkFaint,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 118,
            display: 'inline-block',
          }}
        >
          {value ?? '—'}
        </span>
      </Disclosure>
    </div>
  );
}

/**
 * A recruiter listed among the managers because they have none (owner request 2026-10-30). The
 * warning palette, because this is the one thing on the row that says it is a person and not a
 * team — the figures beside it are theirs alone.
 */
function NoManagerTag() {
  return (
    <span
      title="A recruiter with no manager. The figures on this row are this person's own."
      style={{
        fontSize: '9.5px',
        fontWeight: 600,
        padding: '0 5px',
        borderRadius: radius.sm,
        border: `1px solid ${color.warningBorder}`,
        background: color.warningBg,
        color: color.warning,
        whiteSpace: 'nowrap',
      }}
    >
      No manager
    </span>
  );
}

function PersonCell({
  name,
  fallback,
  accentKey,
  sub,
}: {
  name: string | null;
  fallback: string;
  accentKey: string;
  sub: React.ReactNode;
}) {
  const accent = personAccent(accentKey);
  const display = name ?? fallback;

  return (
    <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 9 }}>
      <div
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          flex: 'none',
          borderRadius: radius.xl,
          background: accent,
          color: color.onAccent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10.5px',
          fontWeight: 700,
        }}
      >
        {initialsOf(display)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: fontSize.captionStrong,
            fontWeight: 600,
            color: color.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {display}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manager table (US1, US2)
// ---------------------------------------------------------------------------

export interface DeliveryManagerTableProps {
  rows: DeliveryManagerRow[];
  /**
   * Recruiters with no manager, listed among the managers and marked `No manager` (owner request
   * 2026-10-30). Each row is that person's own figures; a click opens their recruiter page.
   */
  unmanaged?: DeliveryRecruiterRow[];
  combo: string;
  totals?: MetricsBlock;
  unattributedBooks: UnattributedBooks | null | undefined;
  onSelectManager: (managerId: number) => void;
  onSelectRecruiter?: (recruiterId: number) => void;
  /**
   * Renders the per-row export control, when the page supplies one (FR-D27).
   *
   * ⚠ **A render prop, not an `(managerId) => void` callback.** The control is a `<Disclosure>`
   * owning streaming progress, a Google consent round trip and a completion dialog — state this
   * table has no business holding, and which must not be thrown away when a sort re-renders the
   * rows. Passing the node in keeps that with the page and keeps this a table.
   *
   * Omitted, the column does not exist at all, so a caller that does not want one gains no empty
   * track.
   */
  onExportManager?: (row: DeliveryManagerRow) => React.ReactNode;
  /** The same control for an `unmanaged` row — a recruiter's export, not a team's. */
  onExportRecruiter?: (row: DeliveryRecruiterRow) => React.ReactNode;
  emptyMessage?: string;
  header?: React.ReactNode;
}

/** One row of the managers list: a manager, or a recruiter who has none. */
type ListEntity =
  | ({ kind: 'manager' } & DeliveryManagerRow)
  | ({ kind: 'recruiter' } & DeliveryRecruiterRow);

export function DeliveryManagerTable({
  rows,
  unmanaged = [],
  combo,
  totals,
  unattributedBooks,
  onSelectManager,
  onSelectRecruiter,
  onExportManager,
  onExportRecruiter,
  emptyMessage = 'No managers in this selection.',
  header,
}: DeliveryManagerTableProps) {
  const entities: ListEntity[] = [
    ...rows.map((row) => ({ kind: 'manager' as const, ...row })),
    ...unmanaged.map((row) => ({ kind: 'recruiter' as const, ...row })),
  ];
  const tableRows: Array<Row<ListEntity>> = entities.map((entity) => ({
    entity,
    metrics: entity.metricsByCombo?.[combo] ?? entity.metrics ?? {},
    siblings: siblingsOf(entity.metricsByCombo, combo),
  }));

  const columns: Array<Column<Row<ListEntity>>> = [
    {
      key: 'manager',
      label: 'MANAGER',
      fullLabel:
        'Manager — name, with the regions their team covers. Recruiters with no manager are listed among them, marked No manager',
      width: '190px',
      align: 'left',
      render: (row) =>
        row.entity.kind === 'manager' ? (
          <PersonCell
            name={row.entity.name}
            fallback={`Manager ${row.entity.managerId}`}
            accentKey={String(row.entity.managerId)}
            sub={<RegionCodes codes={row.entity.regionCodes} />}
          />
        ) : (
          <PersonCell
            name={row.entity.name}
            fallback={row.entity.email}
            accentKey={String(row.entity.recruiterId)}
            sub={
              <>
                <NoManagerTag />
                <RegionCodes codes={row.entity.regionCode ? [row.entity.regionCode] : []} />
              </>
            }
          />
        ),
      sortValue: (row) =>
        row.entity.kind === 'manager'
          ? (row.entity.name ?? String(row.entity.managerId))
          : (row.entity.name ?? row.entity.email),
    },
    // A recruiter row has no `recruiters*` keys — a person is not a headcount — so those three
    // cells read `—` on it, which is the honest answer rather than a 1.
    ...MANAGER_SPECS.map((spec) => metricColumn<ListEntity>(spec, unattributedBooks, totals)),
    ...(onExportManager
      ? [
          actionColumn<ListEntity>(
            (entity) =>
              entity.kind === 'manager'
                ? onExportManager(entity)
                : (onExportRecruiter?.(entity) ?? null),
            'Action — export this manager’s (or this recruiter’s) submissions to a Google Sheet',
          ),
        ]
      : []),
  ];

  return (
    <DataTable<Row<ListEntity>>
      caption="Managers"
      columns={columns}
      rows={tableRows}
      // Prefixed: a manager id and a recruiter id share one number space (Nexus), and 17 managers
      // have a recruiter record under the SAME id.
      rowKey={(row) =>
        row.entity.kind === 'manager'
          ? `manager:${row.entity.managerId}`
          : `recruiter:${row.entity.recruiterId}`
      }
      onRowClick={(row) =>
        row.entity.kind === 'manager'
          ? onSelectManager(row.entity.managerId)
          : onSelectRecruiter?.(row.entity.recruiterId)
      }
      // Ranked on open, so the largest and thinnest books are obvious without a click (§6.6).
      initialSort={{ key: 'activeToa', direction: 'desc' }}
      showTotals={totals != null}
      minWidth={`${1900 + extraWidthPx(MANAGER_SPECS) + (onExportManager ? ACTION_COLUMN_WIDTH : 0)}px`}
      emptyMessage={emptyMessage}
      header={header}
    />
  );
}

// ---------------------------------------------------------------------------
// Recruiter table (US3)
// ---------------------------------------------------------------------------

export interface DeliveryRecruiterTableProps {
  rows: DeliveryRecruiterRow[];
  combo: string;
  totals?: MetricsBlock;
  unattributedBooks: UnattributedBooks | null | undefined;
  onSelectRecruiter: (recruiterId: number) => void;
  /** Renders the per-row export control — see `onExportManager` (FR-D27). */
  onExportRecruiter?: (row: DeliveryRecruiterRow) => React.ReactNode;
  /** The row whose figures the chart is currently scoped to (FR-D10). */
  focusedRecruiterId?: number | null;
  emptyMessage?: string;
  header?: React.ReactNode;
}

export function DeliveryRecruiterTable({
  rows,
  combo,
  totals,
  unattributedBooks,
  onSelectRecruiter,
  onExportRecruiter,
  focusedRecruiterId,
  emptyMessage = 'No recruiters match this filter.',
  header,
}: DeliveryRecruiterTableProps) {
  const tableRows: Array<Row<DeliveryRecruiterRow>> = rows.map((entity) => ({
    entity,
    metrics: entity.metricsByCombo?.[combo] ?? entity.metrics ?? {},
    siblings: siblingsOf(entity.metricsByCombo, combo),
  }));

  const columns: Array<Column<Row<DeliveryRecruiterRow>>> = [
    {
      key: 'recruiter',
      label: 'RECRUITER',
      fullLabel: 'Recruiter — name, offering, and region',
      width: '190px',
      align: 'left',
      render: (row) => (
        <PersonCell
          name={row.entity.name}
          fallback={row.entity.email}
          accentKey={String(row.entity.recruiterId)}
          sub={
            <>
              <span
                style={{
                  fontSize: '9.5px',
                  fontWeight: 600,
                  padding: '1px 6px',
                  borderRadius: radius.sm,
                  background: row.entity.offering === 'nursing' ? color.washMint : color.washIndigo,
                  color: row.entity.offering === 'nursing' ? color.nursing : color.allied,
                }}
              >
                {row.entity.offering === 'nursing' ? 'Nursing' : 'Allied'}
              </span>
              <RegionCodes codes={row.entity.regionCode ? [row.entity.regionCode] : []} />
            </>
          }
        />
      ),
      sortValue: (row) => row.entity.name ?? row.entity.email,
    },
    {
      key: 'primaryAccount',
      label: 'PRIMARY',
      fullLabel: 'Primary target account',
      width: '130px',
      align: 'left',
      render: (row) => <AccountCell value={row.entity.primaryAccount} />,
      sortValue: (row) => row.entity.primaryAccount,
    },
    {
      key: 'secondaryAccount',
      label: 'SECONDARY',
      fullLabel: 'Secondary target account',
      width: '130px',
      align: 'left',
      render: (row) => <AccountCell value={row.entity.secondaryAccount} />,
      sortValue: (row) => row.entity.secondaryAccount,
    },
    /*
     * The sheet's `3rd Target Account` tier (owner ruling 2026-10-29) — 14 recruiters carry one.
     * Its own column rather than folded into SECONDARY, because a recruiter can hold both and a
     * column that shows whichever came first would lose the other.
     */
    {
      key: 'thirdAccount',
      label: '3RD',
      fullLabel: '3rd target account',
      width: '130px',
      align: 'left',
      render: (row) => <AccountCell value={row.entity.thirdAccount ?? null} />,
      sortValue: (row) => row.entity.thirdAccount ?? null,
    },
    ...RECRUITER_SPECS.map((spec) =>
      metricColumn<DeliveryRecruiterRow>(spec, unattributedBooks, totals),
    ),
    ...(onExportRecruiter ? [actionColumn<DeliveryRecruiterRow>(onExportRecruiter, 'Action — export this recruiter’s submissions to a Google Sheet')] : []),
  ];

  return (
    <DataTable<Row<DeliveryRecruiterRow>>
      caption="Recruiters"
      columns={columns}
      rows={tableRows}
      rowKey={(row) => String(row.entity.recruiterId)}
      // Selecting a row scopes the CHART to that recruiter and leaves this table on screen
      // (FR-D10). It is not navigation, which is why the row is highlighted rather than replaced.
      onRowClick={(row) => onSelectRecruiter(row.entity.recruiterId)}
      isRowSelected={(row) => row.entity.recruiterId === focusedRecruiterId}
      initialSort={{ key: 'activeToa', direction: 'desc' }}
      showTotals={totals != null}
      // +130 is the 3RD account column; the table already scrolls sideways at 1,900.
      minWidth={`${1900 + 130 + extraWidthPx(RECRUITER_SPECS) + (onExportRecruiter ? ACTION_COLUMN_WIDTH : 0)}px`}
      emptyMessage={emptyMessage}
      header={header}
    />
  );
}
